import React from 'react';
import produce from 'immer';
import { withTranslation } from 'react-i18next';
import Input from '../../../../../shared/design-system/components/input';
import Modal from '../../../../../shared/design-system/components/atoms/modal';
import { validate } from './validator';
import type { IProps, IState } from './types';
import { Images } from '../../../../../shared/app-constants';
import { getTestEmailAddressFromLocalStorage } from '../email-modal/helpers';

class SendTestEmailModal extends React.Component<IProps, IState> {
  private readonly focusInputRef: React.RefObject<HTMLInputElement>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      values: { email: '' },
      errors: { email: '' },
      dirty: { email: false },
    };

    this.focusInputRef = React.createRef<HTMLInputElement>();

    this.onInputChange = this.onInputChange.bind(this);
    this.onInputBlur = this.onInputBlur.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onModalShow = this.onModalShow.bind(this);
  }

  componentDidMount() {
    const savedTestEmails = getTestEmailAddressFromLocalStorage();
    if (savedTestEmails) {
      this.setState(
        produce((draft) => {
          draft.values.email = savedTestEmails;
          draft.dirty.email = true;
        }),
      );
    }
  }

  onInputChange(value, e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        draft.values[name] = value;
        draft.dirty[name] = true;
      }),
    );
  }

  onInputBlur(e) {
    const { name } = e.target;
    this.setState(
      produce((draft) => {
        if (draft.dirty[name]) {
          draft.errors[name] = validate(name, draft.values[name]);
        }
      }),
    );
  }

  onFormSubmit(e) {
    e.preventDefault();

    const { dirty, errors, values } = this.state;
    const { onSubmit } = this.props;

    const dirtyRef = { ...dirty };
    const dirtyKeys = Object.keys(dirtyRef);

    dirtyKeys.forEach((key) => {
      dirtyRef[key] = true;
    });

    const errorsRef = { ...errors };
    const errorsKeys = Object.keys(errorsRef);
    let isError = false;

    errorsKeys.forEach((key) => {
      const error = validate(key, values[key]);

      errorsRef[key] = error;
      isError = isError || !!error;
    });

    this.setState({ errors: errorsRef, dirty: dirtyRef });

    if (isError) {
      return;
    }

    onSubmit(values.email, e);
  }

  onModalShow() {
    const { showInput } = this.props;
    if (showInput) {
      this.focusInputRef.current.focus();
    }
  }

  render() {
    const { values, errors } = this.state;
    const {
      show,
      title,
      content,
      onClose,
      onSubmit,
      label,
      showInput,
      submitButtonText,
      cancelButtonText,
      isSubmitLoading,
      isSubmitDisabled,
      className,
      t,
    } = this.props;

    return (
      <Modal
        show={show}
        className={`confirm-modal-width send-test-email-modal ${className}`}
        titleContent={title}
        showCloseIcon
        onClose={onClose}
        onSubmit={showInput ? this.onFormSubmit : onSubmit}
        isSubmitLoading={isSubmitLoading}
        isSubmitDisabled={isSubmitDisabled}
        submitButtonText={submitButtonText}
        cancelButtonText={cancelButtonText}
        backdrop="static"
        extraModalProps={{
          centered: true,
          'aria-labelledby': 'contained-modal-title-vcenter',
          onShow: this.onModalShow,
        }}
      >
        {content.length > 0 &&
          content.map(({ id, text }) => (
            <p key={id} className="send-test-email-modal-content">
              {t(`messages.${text}`)}
            </p>
          ))}

        {showInput && (
          <div className="form-wrapper">
            <p className="send-test-email-modal-label regular-1">{label}</p>
            <Input
              name="email"
              className="send-test-email-modal-input"
              value={values.email}
              variant={errors.email && Input.Variant.Error}
              caption={
                errors.email && (
                  <>
                    <img src={Images.AlertTriangleRed} alt="Alert" />
                    {errors.email}
                  </>
                )
              }
              onChange={this.onInputChange}
              onBlur={this.onInputBlur}
              autoComplete="off"
              ref={this.focusInputRef}
            />
          </div>
        )}
      </Modal>
    );
  }
}

export default withTranslation()(SendTestEmailModal);
